import { useDispatch, useSelector } from 'react-redux';
import i18next from 'i18next';
import { seriesToObject, isViaModelSeries } from '@/containers/CampaignDetail/AffectedVehicles/SeriesPanel/SeriesRow/series-row-utils';
import { mapUpdateVolumeData, getPlantSelectVolume } from '@/containers/CampaignDetail/AffectedVehicles/Volume/VolumeDetails/volumeDetails-utils';
import { buildFeedbackSavePayload } from '@/containers/SubCampaignDetail/Feedback/feedback-utils';
import { errorTypes, stateTabs, WARNING, ERROR } from '@/helpers/constants';
import { getErrorCode, mapUpdateTranslationsData } from '@/helpers/utils';
import { setAffectedVehiclesVolumes, setOriginalValues, setPendingVolumes } from '@/redux/Campaign/campaign.action';
import { setSubCampaignOriginalValues } from '@/redux/SubCampaign/subCampaign.action';
import campaignServices from '@/services/campaignServices';
import useApiRequest from './useApiRequest';
import useToastContext from './useToastContext';

const mapData = (data, tabParam) => ({ data, tabParam });

const getSaveParams = (tab, campaign, endPoint) => {
  if (['translations'].includes(tab)) {
    return mapData(mapUpdateTranslationsData(campaign), 'translations');
  }

  return mapData(campaign[endPoint], tab);
};

const seriesByPlantSeverityByErrorCode = {
  [errorTypes.SERIES_BY_PLANT_NOT_ALLOWED]: ERROR,
  [errorTypes.SERIES_BY_PLANT_PARTIALLY_NOT_ALLOWED]: WARNING,
};

const handleSeriesByPlantErrors = (err, addToast) => {
  const severity = seriesByPlantSeverityByErrorCode[getErrorCode(err)];

  if (!severity) throw err;

  return addToast(i18next.t('affectedVehicles.seriesByPlantNotAllowed'), severity);
};

const saveTechnicalAssessment = async (id, data, originalValues, saveCR, addToast) => {
  const assessmentHasChanges = originalValues?.hasChange && originalValues?.changesIn?.technicalAssessment;

  if (assessmentHasChanges) {
    await saveCR(id, data?.technicalAssessment, 'affected-vehicles/technical-assessment').catch((err) => handleSeriesByPlantErrors(err, addToast));
  }
};

const oldPlantsToNewPlants = (oldPlants) => ({
  affectedPlants: oldPlants.map((plant) => (
    { ...plant, affectedSeries: Object.keys(plant?.affectedSeries).map((ms) => ({ modelSerie: ms, typeVersions: plant?.affectedSeries[ms] })) })),
});
const oldVehiclesToNewVehicles = (oldVehicles) => ({ affectedSeries: Object.keys(oldVehicles?.series || {}).map((ms) => ({ modelSerie: ms, typeVersions: oldVehicles?.series?.[ms].map((tvWithPlant) => ({ typeVersion: tvWithPlant.typeVersion, plants: tvWithPlant.plants })) })) });

const saveAffectedVehiclesPart = async (id, body, endpoint, saveCR, addToast) => saveCR(id, body, `affected-vehicles/${endpoint}`).catch((err) => handleSeriesByPlantErrors(err, addToast));

const saveTypeOfSelection = async (id, data, originalValues, saveCR, addToast) => {
  const previousSelection = originalValues?.values?.typeOfAffectedPlantsSelection;
  const currentSelection = data?.typeOfAffectedPlantsSelection;
  const typeOfSelectionHasChanged = previousSelection ? previousSelection !== currentSelection : false;
  const endpoint = isViaModelSeries(data) ? 'affected-series' : 'affected-plants';
  const seriestHasChanges = originalValues?.hasChange && originalValues.changesIn.series;
  const plantstHasChanges = originalValues?.hasChange && originalValues.changesIn.plants;
  const conditionSave = isViaModelSeries(data) ? seriestHasChanges : plantstHasChanges;
  const seriesUpdated = seriesToObject(data?.series);

  if (typeOfSelectionHasChanged && conditionSave) {
    const newRefactorPlants = oldPlantsToNewPlants(data.affectedPlants);
    const newRefactorVehicles = oldVehiclesToNewVehicles(seriesUpdated);

    await saveAffectedVehiclesPart(id, isViaModelSeries(data) ? newRefactorVehicles : newRefactorPlants, endpoint, saveCR, addToast);
  }

  return { seriestHasChanges, plantstHasChanges, seriesUpdated };
};

const saveSeries = async (id, data, originalValues, saveCR, addToast) => {
  const { seriestHasChanges, plantstHasChanges, seriesUpdated } = await saveTypeOfSelection(id, data, originalValues, saveCR, addToast);
  const endpoint = isViaModelSeries(data) ? 'affected-series' : 'affected-plants';
  const conditionSave = isViaModelSeries(data) ? seriestHasChanges : plantstHasChanges;
  const newRefactorPlants = oldPlantsToNewPlants(data.affectedPlants);
  const newRefactorVehicles = oldVehiclesToNewVehicles(seriesUpdated);

  if (conditionSave) await saveAffectedVehiclesPart(id, isViaModelSeries(data) ? newRefactorVehicles : newRefactorPlants, endpoint, saveCR, addToast);
};

const saveVolumes = async (id, data, originalValues, timeZone, dispatch, saveCR, addToast) => {
  dispatch(setPendingVolumes(true));
  const volumesHasChanges = Object.keys(originalValues?.changesIn?.volumes || {});
  const body = (plant) => mapUpdateVolumeData(getPlantSelectVolume(data.volumes, plant), plant, timeZone);
  const promises = volumesHasChanges.map((plant) => saveCR(id, body(plant), `${plant}/limitation-volume`).catch((err) => handleSeriesByPlantErrors(err, addToast)));
  await Promise.all(promises);
  dispatch(setPendingVolumes(false));
};

// Memoized selector to prevent unnecessary re-renders
const useSave = (tab) => {
  const { addToast } = useToastContext();
  const dispatch = useDispatch();

  // Use memoized selector with specific state selections
  const { id, campaign, timeZone, originalValues } = useSelector(
    (state) => ({
      id: tab !== 'feedback' ? state?.campaignReducer?.info?.id : state.subCampaignReducer?.subCampaignInfo?.id,
      campaign: tab !== 'feedback' ? state.campaignReducer : state.subCampaignReducer,
      timeZone: state.userReducer.settings?.timezone,
      originalValues: tab !== 'feedback' ? state.campaignReducer.originalValues : state.subCampaignReducer.originalValues,
    }),
    // Shallow equality check to prevent unnecessary re-renders
    (left, right) => left.id === right.id
      && left.campaign === right.campaign
      && left.timeZone === right.timeZone
      && left.originalValues === right.originalValues,
  );
  const saveCR = useApiRequest(campaignServices.saveCampaign);

  const endPoint = stateTabs[tab];
  const { data, tabParam } = getSaveParams(tab, campaign, endPoint);
  const saveChanges = async () => {
    try {
      if (tab === 'affected-vehicles') {
        await saveTechnicalAssessment(id, data, originalValues, saveCR, addToast);
        await saveSeries(id, data, originalValues, saveCR, addToast);
        await saveVolumes(id, data, originalValues, timeZone, dispatch, saveCR, addToast);
      } else if (originalValues?.hasChange) {
        if (tab === 'feedback') {
          await campaignServices.putFeedback(id, buildFeedbackSavePayload(data)).catch((err) => {
            console.log(err);
          });
        } else {
          await saveCR(id, data, tabParam, campaign.info.type, campaign.info.creationDate).catch((error) => {
            if (getErrorCode(error) === errorTypes.BOTH_CODING_AND_FLASHING_ACTIVE) {
              addToast(i18next.t('software.flashingCodingSwitchErrorMessage'), 'ERROR');

              return;
            }
            handleSeriesByPlantErrors(error, addToast);
          });
        }
      }

      const savedValues = { values: data, hasChange: false, changesIn: {} };

      if (tab === 'feedback') {
        dispatch(setSubCampaignOriginalValues(savedValues));
      } else {
        dispatch(setOriginalValues(savedValues));
      }
    } catch (err) {
      const errorCode = getErrorCode(err);

      if (errorCode === errorTypes.MODEL_SERIES_COMBINATION_ERROR) {
        addToast(i18next.t('affectedVehicles.modelSeriesCombinationError'), 'ERROR');
        dispatch(setAffectedVehiclesVolumes(originalValues.volumes));
        dispatch(setPendingVolumes(true));
      } else {
        throw err;
      }
      dispatch(setPendingVolumes(false));
    }
  };

  return saveChanges;
};

export default useSave;
